import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Autoplay } from 'swiper'
import 'swiper/swiper-bundle.min.css'
import 'swiper/swiper.min.css'
import CarouselControls from './CarouselControls'
import { Box } from 'shared'
import Confetti from '../Confetti'

interface CarouselProps {
  slidesData: {
    type?: string
    title?: string
    description?: string
    slideId?: string
  }[]
  slidesImages: string[]
  SlideComponent: React.ComponentType<any>
  slidesPerView?: number
  spaceBetween?: number
  mobileButtons?: boolean
  autoScroll?: boolean
  ProgressComponent?: React.ComponentType<any>
  confetti?: boolean
  progressBarText?: string
}

export default function Carousel({
  slidesData,
  slidesPerView = 3,
  spaceBetween = 50,
  slidesImages,
  ProgressComponent,
  SlideComponent,
  mobileButtons,
  autoScroll,
  confetti,
  progressBarText,
}: CarouselProps) {
  SwiperCore.use([Autoplay])

  const [showConfetti, setShowConfetti] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [swiperKey, setSwiperKey] = useState(0)
  const carouselRef = useRef<SwiperCore | null>(null)
  const totalSlides = slidesData.length
  const canGoPrev = currentIndex > 0
  const canGoNext = currentIndex + slidesPerView < totalSlides

  const goToNext = () => {
    carouselRef.current?.slideNext()
  }

  const goToPrev = () => {
    carouselRef.current?.slidePrev()
  }

  const toggleConfetti = () => {
    setShowConfetti(true)
    setTimeout(() => {
      setShowConfetti(false)
    }, 1000)
  }

  useEffect(() => {
    setCurrentIndex(0)
    setSwiperKey((prevKey) => prevKey + 1)
  }, [slidesData])

  return (
    <>
      {confetti && <Confetti start={showConfetti} variant="top" />}
      <Swiper
        key={swiperKey}
        breakpoints={{
          1024: {
            slidesPerView: slidesPerView,
            spaceBetween: spaceBetween,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          640: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
        }}
        onSwiper={(swiper) => (carouselRef.current = swiper)}
        onReachEnd={() => {
          toggleConfetti()
        }}
        onSlideChange={(swiper) => {
          setCurrentIndex(swiper.activeIndex)
        }}
        autoplay={autoScroll ? { delay: 4000, disableOnInteraction: true } : false}
      >
        <Box alignSelf="center">
          {slidesData.map((slideData, index) => (
            <SwiperSlide key={index} style={{ height: 'auto', alignSelf: 'stretch' }}>
              <SlideComponent imgSrc={slidesImages[index]} {...slideData} />
            </SwiperSlide>
          ))}
        </Box>
      </Swiper>
      <CarouselControls
        currentIndex={currentIndex}
        totalSlides={slidesData.length}
        visibleSlidesCount={slidesPerView}
        goToNext={goToNext}
        goToPrev={goToPrev}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        renderProgress={ProgressComponent}
        mobileButtons={mobileButtons}
        progressBarText={progressBarText}
      />
    </>
  )
}
